// React
import React from 'react'

// Next
import Head from 'next/head'
import { GetStaticProps } from 'next'

// Components
import Layout from '@/components/website/core/Layout'
import TextSection from '@/components/website/core/TextSection'
import TopicMemes from '@/components/website/TopicMemes'
import CTAComponent from '@/components/website/core/CTA'
import YouMightAlsoLike from '@/components/website/YouMightAlsoLike'
import LinkedText from '@/components/website/core/LinkedText'

// Utils
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'

// External
import { useTranslation } from 'next-i18next'

// Helper function to handle structured content
const renderStructuredContent = (content: any): string | React.ReactNode => {
  if (typeof content === 'string') {
    return content
  }
  if (content && typeof content === 'object') {
    return <LinkedText content={content} />
  }
  return ''
}

const SalesMemes: React.FC = () => {
  const { t } = useTranslation('routes/sales-memes')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('meta.title'),
    description: t('meta.description'),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://supermeme.ai/memes/sales-memes',
    },
    publisher: {
      '@type': 'Organization',
      name: 'SuperMeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
  }

  return (
    <Layout>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta
          property='og:url'
          content='https://supermeme.ai/memes/sales-memes'
        />
        <link rel='canonical' href='https://supermeme.ai/memes/sales-memes' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <TopicMemes
        heading={t('hero.heading')}
        description={t('hero.description')}
        imageFolder='static/landing-pages/sales-memes'
      />

      <div className='mx-auto max-w-4xl space-y-8 py-12 sm:py-16'>
        <YouMightAlsoLike />

        <TextSection h2={t('about.title')} />

        <TextSection
          h3={t('whatAre.title')}
          p={renderStructuredContent(
            t('whatAre.content', { returnObjects: true })
          )}
        />

        <TextSection
          h3={t('whyUse.title')}
          p={
            <div className='space-y-4'>
              {(
                t('whyUse.paragraphs', { returnObjects: true }) as string[]
              ).map((paragraph, index) => (
                <p key={index}>{renderStructuredContent(paragraph)}</p>
              ))}
            </div>
          }
        />

        <TextSection
          h3={t('howToUse.title')}
          p={
            <div className='space-y-4'>
              {(
                t('howToUse.paragraphs', { returnObjects: true }) as string[]
              ).map((paragraph, index) => (
                <p key={index}>{renderStructuredContent(paragraph)}</p>
              ))}
            </div>
          }
        />

        <TextSection
          h2={t('using.title')}
          p={renderStructuredContent(
            t('using.content', { returnObjects: true })
          )}
        />

        <TextSection
          h3={t('coldEmails.title')}
          p={renderStructuredContent(
            t('coldEmails.content', { returnObjects: true })
          )}
        />

        <TextSection
          h3={t('socialMedia.title')}
          p={renderStructuredContent(
            t('socialMedia.content', { returnObjects: true })
          )}
        />

        <TextSection
          h3={t('presentations.title')}
          p={renderStructuredContent(
            t('presentations.content', { returnObjects: true })
          )}
        />

        <TextSection h2={t('creating.title')} />

        <TextSection
          h3={t('tools.title')}
          p={renderStructuredContent(
            t('tools.content', { returnObjects: true })
          )}
        />

        <TextSection
          h3={t('aiCreated.title')}
          p={renderStructuredContent(
            t('aiCreated.content', { returnObjects: true })
          )}
        />

        <CTAComponent />
      </div>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: ['common', 'routes/sales-memes', 'components/cta'],
  })
}

export default SalesMemes
